import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, till } from "@/lib/api";
import { Button, Field, Input, Modal, Select, useToast } from "./ui";

export type Supplier = { id: string; name: string; phone: string | null; contact: string | null; deliveries: number; lastAt: number | null };
export type Suppliers = { suppliers: Supplier[]; lastPaid: { itemId: string; supplierId: string; cost: number }[] };

export function useSuppliers() {
  return useQuery({ queryKey: ["suppliers"], queryFn: () => api<Suppliers>(till("/admin/suppliers")), staleTime: 30_000 });
}

/** The latest price paid for a product from a supplier, or null. */
export function lastPaidOf(data: Suppliers | undefined, itemId: string, supplierId: string | null | undefined): number | null {
  if (!data || !supplierId) return null;
  return data.lastPaid.find((x) => x.itemId === itemId && x.supplierId === supplierId)?.cost ?? null;
}

/** Add or edit a supplier. */
export function SupplierModal({ person, onClose, onSaved }: { person?: Supplier | null; onClose: () => void; onSaved?: (id: string) => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [f, setF] = useState({ name: person?.name ?? "", phone: person?.phone ?? "", contact: person?.contact ?? "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = person
        ? await api<{ id: string }>(till(`/admin/suppliers/${person.id}`), { method: "PUT", body: f })
        : (await api<{ supplier: { id: string } }>(till("/admin/suppliers"), { body: f })).supplier;
      await qc.refetchQueries({ queryKey: ["suppliers"] });
      toast("Supplier saved");
      onSaved?.(r.id);
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't save", "err");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={person ? `Edit ${person.name}` : "Add a supplier"} onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button tone="primary" disabled={busy || !f.name.trim()} onClick={() => void save()}>Save</Button></>}>
      <div className="grid gap-3">
        <Field label="Supplier name"><Input value={f.name} maxLength={80} autoFocus onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label="Phone number"><Input inputMode="tel" value={f.phone} maxLength={30} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
        <Field label="Contact person"><Input value={f.contact} maxLength={80} onChange={(e) => setF({ ...f, contact: e.target.value })} /></Field>
      </div>
    </Modal>
  );
}

/** A dropdown of suppliers, with adding a new one. */
export function SupplierSelect({ label, value, onChange, blank, hint }: { label: string; value: string; onChange: (id: string) => void; blank: string; hint?: string }) {
  const q = useSuppliers();
  const [adding, setAdding] = useState(false);
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[13px] font-semibold">{label}</span>
      <Select aria-label={label} value={value} onChange={(e) => (e.target.value === "__new" ? setAdding(true) : onChange(e.target.value))}>
        <option value="">{blank}</option>
        {(q.data?.suppliers ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        <option value="__new">+ Add a new supplier</option>
      </Select>
      {hint ? <span className="text-xs text-slate-500">{hint}</span> : null}
      {adding ? <SupplierModal onClose={() => setAdding(false)} onSaved={onChange} /> : null}
    </div>
  );
}

// ─────────────────────────── the Sold by list ───────────────────────────
export type Unit = { name: string; count: number };
export const useUnits = () => useQuery({ queryKey: ["units"], queryFn: () => api<{ units: Unit[] }>(till("/admin/units")), staleTime: 60_000 });

/** Sold by: picks from the shop's own list, with typing to add a new one. An empty value means "piece". */
export function UnitSelect({ value, onChange, hint }: { value: string; onChange: (v: string) => void; hint?: string }) {
  const toast = useToast();
  const q = useUnits();
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState("");
  const current = value || "piece";
  const names = [...new Set([...(q.data?.units ?? []).map((u) => u.name), current])];
  const add = async () => {
    const name = adding.trim().toLowerCase();
    if (!name) return;
    try {
      await api(till("/admin/units"), { body: { name } });
      await q.refetch();
      onChange(name);
      setOpen(false);
      setAdding("");
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't add it", "err");
    }
  };
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[13px] font-semibold">Sold by</span>
      {open ? (
        <div className="flex gap-2">
          <Input autoFocus aria-label="New unit" value={adding} maxLength={30} onChange={(e) => setAdding(e.target.value)} placeholder="New unit, e.g. drum" className="min-w-0 flex-1" />
          <Button onClick={() => void add()}>Add</Button>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
        </div>
      ) : (
        <Select aria-label="Sold by" value={current} onChange={(e) => (e.target.value === "__new" ? setOpen(true) : onChange(e.target.value === "piece" ? "" : e.target.value))}>
          {names.map((n) => <option key={n} value={n}>{n[0]!.toUpperCase() + n.slice(1)}</option>)}
          <option value="__new">+ Add a new unit</option>
        </Select>
      )}
      {hint ? <span className="text-xs text-slate-500">{hint}</span> : null}
    </div>
  );
}
